import React, { useEffect, useRef } from 'react';
import { logArEvent } from '../../features/ar/logger';
import { registerAframeComponents } from '../../features/ar/registerAframeComponents';
import type { ArImageTrackingConfig, ArMarkerTrackingConfig, ArTrackingMode, ResolvedArExperience } from '../../features/ar/types';

type SceneEventHandlers = {
    onScanning: () => void;
    onTargetFound: () => void;
    onTargetLost: () => void;
    onModelLoading: () => void;
    onModelLoaded: () => void;
    onModelError: () => void;
    onRuntimeError: () => void;
    onSessionDestroyed: () => void;
    onCameraFeedReady: () => void;
};

type ArTrackingSceneProps = SceneEventHandlers & {
    mode: ArTrackingMode;
    experience: ResolvedArExperience;
    sceneKey: number;
};

const attachVisibility = (element: HTMLElement | null, visible: boolean) => {
    if (!element) {
        return;
    }

    element.setAttribute('visible', visible ? 'true' : 'false');
};

const destroySceneResources = (sceneElement: HTMLElement | null) => {
    if (!sceneElement) {
        return;
    }

    const containerElement = sceneElement.parentElement;
    const anyScene = sceneElement as any;
    const systems = anyScene.systems || {};

    try {
        systems['mindar-image-system']?.stop?.();
        systems.arjs?.stop?.();
        systems.arjs?.pause?.();
        anyScene.pause?.();
    } catch (error) {
        logArEvent('warn', 'session_destroy_partial_failure', {
            message: error instanceof Error ? error.message : 'unknown',
        });
    }

    const mediaElements = new Set<HTMLVideoElement>();
    sceneElement.querySelectorAll('video').forEach((video) => mediaElements.add(video as HTMLVideoElement));
    containerElement?.querySelectorAll('video').forEach((video) => mediaElements.add(video as HTMLVideoElement));

    mediaElements.forEach((video) => {
        const stream = video.srcObject as MediaStream | null;
        stream?.getTracks().forEach((track) => track.stop());
        video.srcObject = null;
        if (video.classList.contains('gastroflow-ar-video')) {
            video.remove();
        }
    });
};

const configureSceneVideo = (sceneElement: HTMLElement, onCameraFeedReady: () => void) => {
    let observer: MutationObserver | null = null;
    const containerElement = sceneElement.parentElement;

    const applyVideoSettings = (video: HTMLVideoElement | null) => {
        if (!video) {
            return false;
        }

        video.setAttribute('autoplay', 'true');
        video.setAttribute('muted', 'true');
        video.setAttribute('playsinline', 'true');
        video.setAttribute('webkit-playsinline', 'true');
        video.muted = true;
        video.playsInline = true;
        video.classList.add('gastroflow-ar-video');
        video.style.position = 'absolute';
        video.style.inset = '0';
        video.style.objectFit = 'cover';
        video.style.width = '100%';
        video.style.height = '100%';
        video.style.display = 'block';
        video.style.visibility = 'visible';
        video.style.opacity = '1';
        video.style.zIndex = '0';
        video.style.pointerEvents = 'none';
        sceneElement.style.background = 'transparent';
        onCameraFeedReady();
        return true;
    };

    const findRuntimeVideo = () =>
        sceneElement.querySelector<HTMLVideoElement>('video') ||
        containerElement?.querySelector<HTMLVideoElement>('video') ||
        document.querySelector<HTMLVideoElement>('video');

    if (!applyVideoSettings(findRuntimeVideo())) {
        observer = new MutationObserver(() => {
            if (applyVideoSettings(findRuntimeVideo())) {
                observer?.disconnect();
            }
        });

        observer.observe(containerElement || sceneElement, { childList: true, subtree: true });
    }

    return () => observer?.disconnect();
};

const ArTrackingScene: React.FC<ArTrackingSceneProps> = ({
    mode,
    experience,
    sceneKey,
    onScanning,
    onTargetFound,
    onTargetLost,
    onModelLoading,
    onModelLoaded,
    onModelError,
    onRuntimeError,
    onSessionDestroyed,
    onCameraFeedReady,
}) => {
    const modelLoadTimeoutRef = useRef<number | null>(null);
    const sceneRef = useRef<HTMLElement | null>(null);
    const targetRef = useRef<HTMLElement | null>(null);
    const modelRef = useRef<HTMLElement | null>(null);
    const handlersRef = useRef<SceneEventHandlers>({
        onScanning,
        onTargetFound,
        onTargetLost,
        onModelLoading,
        onModelLoaded,
        onModelError,
        onRuntimeError,
        onSessionDestroyed,
        onCameraFeedReady,
    });

    useEffect(() => {
        registerAframeComponents();
    }, []);

    useEffect(() => {
        handlersRef.current = {
            onScanning,
            onTargetFound,
            onTargetLost,
            onModelLoading,
            onModelLoaded,
            onModelError,
            onRuntimeError,
            onSessionDestroyed,
            onCameraFeedReady,
        };
    }, [
        onCameraFeedReady,
        onModelError,
        onModelLoaded,
        onModelLoading,
        onRuntimeError,
        onScanning,
        onSessionDestroyed,
        onTargetFound,
        onTargetLost,
    ]);

    useEffect(() => {
        const sceneElement = sceneRef.current;
        const targetElement = targetRef.current;
        const modelElement = modelRef.current;

        if (!sceneElement || !targetElement || !modelElement) {
            return;
        }

        const readyEvent = mode === 'image-tracking' ? 'arReady' : 'loaded';
        const foundEvent = mode === 'image-tracking' ? 'targetFound' : 'markerFound';
        const lostEvent = mode === 'image-tracking' ? 'targetLost' : 'markerLost';
        const sceneErrorEvent = mode === 'image-tracking' ? 'arError' : 'camera-error';
        const cleanupVideoObserver = configureSceneVideo(sceneElement, () => handlersRef.current.onCameraFeedReady());

        attachVisibility(modelElement, false);
        handlersRef.current.onModelLoading();
        modelLoadTimeoutRef.current = window.setTimeout(() => {
            logArEvent('error', 'model_load_timeout', { itemId: experience.itemId, modelUrl: experience.modelUrl });
            handlersRef.current.onModelError();
        }, 12000);

        const handleSceneReady = () => handlersRef.current.onScanning();
        const handleTargetFound = () => {
            attachVisibility(modelElement, true);
            logArEvent('info', 'target_found', { mode, targetId: experience.targetId });
            handlersRef.current.onTargetFound();
        };
        const handleTargetLost = () => {
            attachVisibility(modelElement, false);
            logArEvent('info', 'target_lost', { mode, targetId: experience.targetId });
            handlersRef.current.onTargetLost();
        };
        const handleModelLoaded = () => {
            if (modelLoadTimeoutRef.current) {
                window.clearTimeout(modelLoadTimeoutRef.current);
                modelLoadTimeoutRef.current = null;
            }
            logArEvent('info', 'model_load_success', { itemId: experience.itemId, modelUrl: experience.modelUrl });
            handlersRef.current.onModelLoaded();
        };
        const handleModelError = () => {
            if (modelLoadTimeoutRef.current) {
                window.clearTimeout(modelLoadTimeoutRef.current);
                modelLoadTimeoutRef.current = null;
            }
            logArEvent('error', 'model_load_failure', { itemId: experience.itemId, modelUrl: experience.modelUrl });
            handlersRef.current.onModelError();
        };
        const handleSceneError = () => {
            logArEvent('error', 'runtime_error', { mode, targetId: experience.targetId });
            handlersRef.current.onRuntimeError();
        };

        sceneElement.addEventListener(readyEvent, handleSceneReady as EventListener);
        sceneElement.addEventListener(sceneErrorEvent, handleSceneError as EventListener);
        targetElement.addEventListener(foundEvent, handleTargetFound as EventListener);
        targetElement.addEventListener(lostEvent, handleTargetLost as EventListener);
        modelElement.addEventListener('model-loaded', handleModelLoaded as EventListener);
        modelElement.addEventListener('model-error', handleModelError as EventListener);

        return () => {
            sceneElement.removeEventListener(readyEvent, handleSceneReady as EventListener);
            sceneElement.removeEventListener(sceneErrorEvent, handleSceneError as EventListener);
            targetElement.removeEventListener(foundEvent, handleTargetFound as EventListener);
            targetElement.removeEventListener(lostEvent, handleTargetLost as EventListener);
            modelElement.removeEventListener('model-loaded', handleModelLoaded as EventListener);
            modelElement.removeEventListener('model-error', handleModelError as EventListener);
            if (modelLoadTimeoutRef.current) {
                window.clearTimeout(modelLoadTimeoutRef.current);
                modelLoadTimeoutRef.current = null;
            }
            cleanupVideoObserver();
            destroySceneResources(sceneElement);
            logArEvent('info', 'session_destroyed', { mode, targetId: experience.targetId });
            handlersRef.current.onSessionDestroyed();
        };
    }, [
        experience,
        mode,
        sceneKey,
    ]);

    const { runtimeConfig, transform } = experience;

    if (mode === 'image-tracking') {
        const imageRuntimeConfig = runtimeConfig as ArImageTrackingConfig;

        return (
            <a-scene
                key={`image-${sceneKey}`}
                ref={sceneRef}
                embedded
                renderer="colorManagement: true; physicallyCorrectLights: true; antialias: true; alpha: true"
                color-space="sRGB"
                vr-mode-ui="enabled: false"
                device-orientation-permission-ui="enabled: false"
                loading-screen="enabled: false"
                mindar-image={`imageTargetSrc: ${imageRuntimeConfig.imageTargetSrc}; autoStart: true; uiLoading: no; uiScanning: no; uiError: no;`}
                className="webar-scene h-full w-full"
            >
                <a-entity light="type: ambient; intensity: 1.1; color: #ffffff" />
                <a-entity light="type: directional; intensity: 0.85; color: #fff2dc" position="1 2 1" />
                <a-camera position="0 0 0" look-controls="enabled: false" />
                <a-entity
                    ref={targetRef}
                    mindar-image-target={`targetIndex: ${imageRuntimeConfig.targetIndex}`}
                >
                    <a-entity
                        ref={modelRef}
                        gltf-model={experience.modelUrl}
                        position={transform.position}
                        rotation={transform.rotation}
                        scale={transform.scale}
                        shadow={`cast: ${transform.enableShadow ? 'true' : 'false'}; receive: ${transform.enableShadow ? 'true' : 'false'}`}
                        gastroflow-model-anchor={`center: ${transform.centerPivot !== false}; alignY: ${transform.alignY || 'floor'}; shadow: ${transform.enableShadow !== false}`}
                    />
                </a-entity>
            </a-scene>
        );
    }

    const markerRuntimeConfig = runtimeConfig as ArMarkerTrackingConfig;

    return (
        <a-scene
            key={`marker-${sceneKey}`}
            ref={sceneRef}
            embedded
            renderer="colorManagement: true; physicallyCorrectLights: true; antialias: true; alpha: true"
            color-space="sRGB"
            vr-mode-ui="enabled: false"
            device-orientation-permission-ui="enabled: false"
            loading-screen="enabled: false"
            arjs="sourceType: webcam; facingMode: environment; debugUIEnabled: false; trackingMethod: best;"
            className="webar-scene h-full w-full"
        >
            <a-entity light="type: ambient; intensity: 1.1; color: #ffffff" />
            <a-entity light="type: directional; intensity: 0.8; color: #fff2dc" position="1 2 1" />
            <a-marker
                ref={targetRef}
                preset={markerRuntimeConfig.markerPreset}
                type={markerRuntimeConfig.markerPatternUrl ? 'pattern' : undefined}
                url={markerRuntimeConfig.markerPatternUrl}
                smooth="true"
                smooth-count="10"
                smooth-tolerance="0.01"
                smooth-threshold="5"
            >
                <a-entity
                    ref={modelRef}
                    gltf-model={experience.modelUrl}
                    position={transform.position}
                    rotation={transform.rotation}
                    scale={transform.scale}
                    shadow={`cast: ${transform.enableShadow ? 'true' : 'false'}; receive: ${transform.enableShadow ? 'true' : 'false'}`}
                    gastroflow-model-anchor={`center: ${transform.centerPivot !== false}; alignY: ${transform.alignY || 'floor'}; shadow: ${transform.enableShadow !== false}`}
                />
            </a-marker>
            <a-entity camera />
        </a-scene>
    );
};

export default ArTrackingScene;
